import { useState, useEffect, useCallback, useRef } from 'react';

const CACHE_MAX_SIZE = 100;

const openSectionsCache = new Map<string, Record<string, boolean>>();

/**
 * 인스펙터 아코디언 섹션 상태를 관리하는 훅.
 * - 아이템 ID별 캐싱 (인스펙터 전환 시 상태 유지)
 * - ref 기반 트리거 (마운트 시 불필요한 동작 방지)
 * - sectionActive 연동 (ControlsHeader의 inactive 섹션은 접기/펼치기에서 제외)
 */
export function useInspectorSections<T extends Record<string, boolean>>({
  itemId,
  defaults,
  collapseAllTrigger,
  expandAllTrigger,
  sectionActive,
}: {
  itemId: string;
  defaults: T;
  collapseAllTrigger: number;
  expandAllTrigger: number;
  sectionActive?: Record<string, boolean>;
}) {
  const defaultsRef = useRef(defaults);
  defaultsRef.current = defaults;

  const [openSections, setOpenSections] = useState<T>(
    () => (openSectionsCache.get(itemId) as T) ?? { ...defaults },
  );

  // 같은 타입의 다른 아이템으로 전환 시 캐시에서 복원
  const prevItemIdRef = useRef(itemId);
  useEffect(() => {
    if (prevItemIdRef.current !== itemId) {
      prevItemIdRef.current = itemId;
      const cached = openSectionsCache.get(itemId) as T | undefined;
      setOpenSections(cached ?? { ...defaultsRef.current });
    }
  }, [itemId]);

  // 상태 변경 시 캐시 업데이트
  useEffect(() => {
    openSectionsCache.set(itemId, openSections);
    if (openSectionsCache.size > CACHE_MAX_SIZE) {
      const oldest = openSectionsCache.keys().next().value;
      if (oldest !== undefined) openSectionsCache.delete(oldest);
    }
  }, [itemId, openSections]);

  // sectionActive를 ref로 관리 (의존성 배열에서 제외)
  const sectionActiveRef = useRef(sectionActive);
  sectionActiveRef.current = sectionActive;

  // ref 기반 트리거: 마운트 시에는 동작하지 않고 실제 버튼 클릭 시에만 동작
  const prevCollapseTrigger = useRef(collapseAllTrigger);
  useEffect(() => {
    if (collapseAllTrigger !== prevCollapseTrigger.current) {
      prevCollapseTrigger.current = collapseAllTrigger;
      const active = sectionActiveRef.current;
      setOpenSections((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (active && key in active) {
            if (active[key]) (next as Record<string, boolean>)[key] = false;
          } else {
            (next as Record<string, boolean>)[key] = false;
          }
        }
        return next;
      });
    }
  }, [collapseAllTrigger]);

  const prevExpandTrigger = useRef(expandAllTrigger);
  useEffect(() => {
    if (expandAllTrigger !== prevExpandTrigger.current) {
      prevExpandTrigger.current = expandAllTrigger;
      const active = sectionActiveRef.current;
      setOpenSections((prev) => {
        const next = { ...prev };
        for (const key of Object.keys(next)) {
          if (active && key in active) {
            if (active[key]) (next as Record<string, boolean>)[key] = true;
          } else {
            (next as Record<string, boolean>)[key] = true;
          }
        }
        return next;
      });
    }
  }, [expandAllTrigger]);

  const toggleSection = useCallback((key: keyof T) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return { openSections, setOpenSections, toggleSection };
}
